import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomeRoute from "@/components/HomeRoute";
import Layout from "@/components/Layout";
import OnboardingTour from "@/components/OnboardingTour";
import AuthPage from "@/pages/AuthPage";
import ActivatePage from "@/pages/ActivatePage";
import HomePage from "@/pages/HomePage";
import MovementsPage from "@/pages/MovementsPage";
import DuePage from "@/pages/DuePage";
import MyMoneyPage from "@/pages/MyMoneyPage";
import AccountsPage from "@/pages/AccountsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import BudgetsPage from "@/pages/BudgetsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import ProfilePage from "@/pages/ProfilePage";
import OnboardingWizard from "@/pages/OnboardingWizard";
import HouseholdPage from "@/pages/HouseholdPage";
import CardsPage from "@/pages/CardsPage";
import FeesPage from "@/pages/FeesPage";
import HelpPage from "@/pages/HelpPage";
import AdminPage from "@/pages/AdminPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CalendarPage from "@/pages/CalendarPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CategoriesPage from "@/pages/CategoriesPage";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

function OnboardingGate({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/onboarding/status").then(r => setStatus(r.data.onboarded))
      .catch(() => setStatus(true));
  }, [user]);
  if (status === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0b0a10]"><Loader2 className="w-7 h-7 text-violet-400 animate-spin"/></div>;
  }
  if (status === false) return <Navigate to="/bienvenido" replace/>;
  return children;
}

// Client-side mirror of the server's binary subscription gate. The API is the
// real enforcement (402 subscription_required); this just avoids rendering a
// gated screen that would 402 anyway. `skip` is for screens that must stay
// reachable WITHOUT a subscription: the activation/value screen itself and the
// billing profile (where the user manages/pays and where Stripe returns).
function SubscriptionGate({ children, skip }) {
  const { user, ready } = useAuth();
  if (skip) return children;
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0b0a10]"><Loader2 className="w-7 h-7 text-violet-400 animate-spin"/></div>;
  }
  if (user && user.has_access === false) return <Navigate to="/activar" replace/>;
  return children;
}

function Protected({ children, skipOnboarding, skipSubscription }) {
  return (
    <ProtectedRoute>
      <SubscriptionGate skip={skipSubscription}>
        {skipOnboarding ? children : <OnboardingGate><Layout>{children}</Layout></OnboardingGate>}
      </SubscriptionGate>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors closeButton />
      </AuthProvider>
    </I18nProvider>
  );
}

function AppRouter() {
  // Google OAuth now completes entirely server-side (own Google Cloud project):
  // the backend sets the auth cookie and redirects straight to PUBLIC_APP_URL,
  // so there's no URL fragment for the SPA to intercept — AuthProvider's normal
  // GET /auth/me on mount picks up the session like any other page load.
  return (
    <Routes>
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public legal pages — no auth, no paywall (static content, gate only covers /api/) */}
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/activar" element={<Protected skipOnboarding skipSubscription><ActivatePage/></Protected>} />
      <Route path="/bienvenido" element={<Protected skipOnboarding><OnboardingWizard/></Protected>} />
      <Route path="/" element={<HomeRoute />} />

      {/* Los cuatro destinos principales. Antes eran diecisiete, uno por
          colección de Mongo. */}
      <Route path="/movimientos" element={<Protected><MovementsPage /></Protected>} />
      <Route path="/por-pagar" element={<Protected><DuePage /></Protected>} />
      <Route path="/mi-dinero" element={<Protected><MyMoneyPage /></Protected>} />
      <Route path="/mi-dinero/cuentas" element={<Protected><AccountsPage /></Protected>} />

      {/* Las rutas viejas siguen resolviendo: hay enlaces guardados, y una URL
          que deja de existir es una pantalla en blanco para quien la abrió. */}
      <Route path="/transacciones" element={<Navigate to="/movimientos" replace />} />
      <Route path="/deudas" element={<Navigate to="/por-pagar" replace />} />
      <Route path="/recurrentes" element={<Navigate to="/por-pagar" replace />} />
      <Route path="/tarjetas" element={<Protected><CardsPage /></Protected>} />
      <Route path="/analisis" element={<Protected><AnalyticsPage /></Protected>} />
      <Route path="/metas" element={<Protected><BudgetsPage /></Protected>} />
      <Route path="/conciliacion" element={<Protected><ReconciliationPage /></Protected>} />
      <Route path="/comisiones" element={<Protected><FeesPage /></Protected>} />
      <Route path="/familia" element={<Protected><HouseholdPage /></Protected>} />
      <Route path="/ayuda" element={<Protected><HelpPage /></Protected>} />
      <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
      <Route path="/calendario" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/suscripciones" element={<Protected><SubscriptionsPage /></Protected>} />
      <Route path="/aprobaciones" element={<Protected><ApprovalsPage /></Protected>} />
      <Route path="/perfil/notificaciones" element={<Protected><NotificationsPage /></Protected>} />
      <Route path="/categorias" element={<Protected><CategoriesPage /></Protected>} />
      <Route path="/perfil" element={<Protected skipSubscription><ProfilePage /></Protected>} />
    </Routes>
  );
}
